/**
 * useVoiceChat — records microphone audio and sends the audio file to the LLM.
 *
 * State machine: idle -> recording -> processing -> speaking -> idle.
 * The browser records WAV input and plays model-generated audio output.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export type VoiceState = 'idle' | 'recording' | 'processing' | 'speaking';

export interface UseVoiceChatOptions {
  /** Called with the completed recording. Caller should upload/send the file. */
  onVoiceRecording: (file: File) => void | Promise<void>;
  /** Called when assistant text should be synthesized by the configured TTS model. */
  onSpeakResponse?: (
    text: string,
    options: { executionId?: string; voice?: string; speed?: number }
  ) => Promise<{ audio_url: string }>;
}

export interface UseVoiceChatReturn {
  voiceState: VoiceState;
  isVoiceOn: boolean;
  interimTranscript: string;
  toggleVoice: () => void;
  speakResponse: (text: string, executionId?: string) => void;
  stopVoice: () => void;
  ttsVoice: string;
  setTtsVoice: (voice: string) => void;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  setSelectedVoice: (voice: SpeechSynthesisVoice) => void;
  voiceRate: number;
  setVoiceRate: (rate: number) => void;
  isRecordingSupported: boolean;
  isTTSSupported: boolean;
}

const LS_VOICE_NAME = 'valpha_model_tts_voice';
const LS_VOICE_RATE = 'valpha_voice_rate';

function loadString(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}

function loadNumber(key: string, fallback: number): number {
  try { const v = localStorage.getItem(key); return v ? Number(v) : fallback; } catch { return fallback; }
}

function persist(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* quota exceeded */ }
}

const isRecordingSupportedGlobal =
  typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && (typeof window.AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined');

function mergeAudioChunks(chunks: Float32Array[]): Float32Array {
  const length = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const result = new Float32Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function writeAscii(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const bytesPerSample = 2;
  const channelCount = 1;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channelCount * bytesPerSample, true);
  view.setUint16(32, channelCount * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, samples.length * bytesPerSample, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += bytesPerSample;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export default function useVoiceChat(options: UseVoiceChatOptions): UseVoiceChatReturn {
  const { onVoiceRecording, onSpeakResponse } = options;

  const onVoiceRecordingRef = useRef(onVoiceRecording);
  onVoiceRecordingRef.current = onVoiceRecording;
  const onSpeakResponseRef = useRef(onSpeakResponse);
  onSpeakResponseRef.current = onSpeakResponse;

  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [ttsVoice, _setTtsVoice] = useState(() => loadString(LS_VOICE_NAME, ''));
  const [voiceRate, _setVoiceRate] = useState(() => loadNumber(LS_VOICE_RATE, 1));

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioChunksRef = useRef<Float32Array[]>([]);
  const playbackRef = useRef<HTMLAudioElement | null>(null);
  const sampleRateRef = useRef(44100);
  const voiceStateRef = useRef<VoiceState>('idle');
  const isVoiceOnRef = useRef(false);
  const cancelRecordingRef = useRef(false);

  voiceStateRef.current = voiceState;

  const isVoiceOn = voiceState !== 'idle';
  const isRecordingSupported = isRecordingSupportedGlobal;
  const isTTSSupported = Boolean(onSpeakResponse);

  const releaseAudioResources = useCallback(() => {
    try { processorRef.current?.disconnect(); } catch { /* ignore */ }
    try { sourceNodeRef.current?.disconnect(); } catch { /* ignore */ }
    processorRef.current = null;
    sourceNodeRef.current = null;
    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== 'closed') {
      void audioContext.close().catch(() => undefined);
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const setTtsVoice = useCallback((voice: string) => {
    const normalized = voice.trim();
    _setTtsVoice(normalized);
    persist(LS_VOICE_NAME, normalized);
  }, []);

  const setVoiceRate = useCallback((rate: number) => {
    const clamped = Math.max(0.5, Math.min(2, rate));
    _setVoiceRate(clamped);
    persist(LS_VOICE_RATE, String(clamped));
  }, []);

  const finishRecording = useCallback(async (blob: Blob) => {
    if (cancelRecordingRef.current || blob.size === 0) {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      setInterimTranscript('');
      return;
    }

    const file = new File([blob], `voice-recording-${Date.now()}.wav`, { type: 'audio/wav' });

    setVoiceState('processing');
    voiceStateRef.current = 'processing';
    setInterimTranscript('');

    try {
      await onVoiceRecordingRef.current(file);
    } catch (error) {
      console.error('[useVoiceChat] voice recording send failed:', error);
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isRecordingSupportedGlobal) return;

    try {
      cancelRecordingRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext: AudioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      sampleRateRef.current = audioContext.sampleRate;
      audioChunksRef.current = [];

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      sourceNodeRef.current = source;
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (cancelRecordingRef.current || voiceStateRef.current !== 'recording') {
          return;
        }
        const input = event.inputBuffer.getChannelData(0);
        audioChunksRef.current.push(new Float32Array(input));
        event.outputBuffer.getChannelData(0).fill(0);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      isVoiceOnRef.current = true;
      setVoiceState('recording');
      voiceStateRef.current = 'recording';
      setInterimTranscript('Recording voice message');
    } catch (error) {
      console.error('[useVoiceChat] failed to start recording:', error);
      releaseAudioResources();
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      setInterimTranscript('');
    }
  }, [finishRecording, releaseAudioResources]);

  const stopRecording = useCallback((submit: boolean) => {
    cancelRecordingRef.current = !submit;
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    releaseAudioResources();
    if (submit && chunks.length > 0) {
      const blob = encodeWav(mergeAudioChunks(chunks), sampleRateRef.current);
      void finishRecording(blob);
      return;
    }
    setVoiceState('idle');
    voiceStateRef.current = 'idle';
    isVoiceOnRef.current = false;
    setInterimTranscript('');
  }, [finishRecording, releaseAudioResources]);

  const speakResponse = useCallback((text: string, executionId?: string) => {
    if (!isVoiceOnRef.current) return;

    const synthesize = onSpeakResponseRef.current;
    if (!synthesize || !text.trim()) {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      return;
    }

    playbackRef.current?.pause();
    setVoiceState('speaking');
    voiceStateRef.current = 'speaking';
    void (async () => {
      try {
        const response = await synthesize(text, {
          executionId,
          voice: ttsVoice || undefined,
          speed: voiceRate,
        });
        if (!isVoiceOnRef.current) return;
        const audio = new Audio(response.audio_url);
        playbackRef.current = audio;
        audio.onended = () => {
          if (playbackRef.current === audio) playbackRef.current = null;
          setVoiceState('idle');
          voiceStateRef.current = 'idle';
          isVoiceOnRef.current = false;
        };
        audio.onerror = () => {
          console.warn('[useVoiceChat] model TTS playback failed');
          if (playbackRef.current === audio) playbackRef.current = null;
          setVoiceState('idle');
          voiceStateRef.current = 'idle';
          isVoiceOnRef.current = false;
        };
        await audio.play();
      } catch (error) {
        console.warn('[useVoiceChat] model TTS failed:', error);
        setVoiceState('idle');
        voiceStateRef.current = 'idle';
        isVoiceOnRef.current = false;
      }
    })();
  }, [ttsVoice, voiceRate]);

  const stopVoice = useCallback(() => {
    isVoiceOnRef.current = false;
    stopRecording(false);
    playbackRef.current?.pause();
    playbackRef.current = null;
    setVoiceState('idle');
    voiceStateRef.current = 'idle';
    setInterimTranscript('');
  }, [stopRecording]);

  const toggleVoice = useCallback(() => {
    if (voiceStateRef.current === 'recording') {
      stopRecording(true);
      return;
    }
    if (voiceStateRef.current !== 'idle') {
      stopVoice();
      return;
    }
    void startRecording();
  }, [startRecording, stopRecording, stopVoice]);

  useEffect(() => {
    return () => {
      isVoiceOnRef.current = false;
      cancelRecordingRef.current = true;
      releaseAudioResources();
      playbackRef.current?.pause();
      playbackRef.current = null;
    };
  }, [releaseAudioResources]);

  return {
    voiceState,
    isVoiceOn,
    interimTranscript,
    toggleVoice,
    speakResponse,
    stopVoice,
    ttsVoice,
    setTtsVoice,
    voices: [],
    selectedVoice: null,
    setSelectedVoice: () => undefined,
    voiceRate,
    setVoiceRate,
    isRecordingSupported,
    isTTSSupported,
  };
}
